// Member weight check-ins (tenant-scoped)
import { ref, push, set, get, update, remove } from 'firebase/database';
import { db, auth } from './config';
import { ACTIVITY_TYPES, logMemberActivity } from './activityLog';
import { parseWeightInput, toKg, WEIGHT_UNITS } from '../utils/weightTracking';

function tenantPath(tenantId, ...segments) {
  return ['tenants', tenantId, ...segments].filter(Boolean).join('/');
}

const getActorLabel = () => auth.currentUser?.email || auth.currentUser?.uid || 'system';

/**
 * Record a new weight reading for a member.
 * Also refreshes the member's current weight so the profile and edit form stay in step.
 */
export const recordWeight = async (tenantId, memberId, reading = {}) => {
  try {
    if (!tenantId || !memberId) {
      return { success: false, error: 'invalid_request', message: 'Missing tenant or member reference' };
    }

    const parsed = parseWeightInput(reading.weight);
    if (!parsed) {
      return { success: false, error: 'invalid_weight', message: 'Enter a valid weight greater than zero.' };
    }

    const unit = reading.unit || parsed.unit || WEIGHT_UNITS.KG;
    const enteredValue = parsed.value;
    const weightKg = toKg(enteredValue, unit);

    if (!Number.isFinite(weightKg) || weightKg <= 0) {
      return { success: false, error: 'invalid_weight', message: 'Enter a valid weight greater than zero.' };
    }

    const measuredAt = reading.date ? new Date(reading.date).toISOString() : new Date().toISOString();

    if (isNaN(new Date(measuredAt).getTime())) {
      return { success: false, error: 'invalid_date', message: 'Enter a valid measurement date.' };
    }

    const memberRef = ref(db, tenantPath(tenantId, 'members', memberId));
    const memberSnapshot = await get(memberRef);

    if (!memberSnapshot.exists()) {
      return { success: false, error: 'not_found', message: 'Member not found' };
    }

    const member = memberSnapshot.val();

    const entry = {
      memberId,
      memberName: member.fullName || '',
      weightKg,
      enteredValue,
      unit,
      measuredAt,
      notes: reading.notes || '',
      recordedBy: getActorLabel(),
      timestamp: Date.now()
    };

    // Stored under the member so reading history needs no query index
    const entryRef = push(ref(db, tenantPath(tenantId, 'measurements', memberId)));
    await set(entryRef, entry);

    // Keep the member's current weight aligned with the newest reading
    await update(memberRef, {
      weight: `${enteredValue} ${unit}`,
      lastWeighInDate: measuredAt
    });

    await logMemberActivity(tenantId, {
      memberId,
      memberName: member.fullName,
      type: ACTIVITY_TYPES.WEIGHT_RECORDED,
      summary: `Weight recorded: ${enteredValue} ${unit}`,
      details: { weightKg, unit, measuredAt, notes: entry.notes }
    });

    return { success: true, message: 'Weight recorded successfully', entryId: entryRef.key, entry };
  } catch (error) {
    console.error('Error recording weight:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to record weight'
    };
  }
};

// All readings for a member, oldest first.
// Reads the member's own subtree directly - no orderByChild, so no index needed.
export const getWeightHistory = async (tenantId, memberId) => {
  try {
    if (!tenantId || !memberId) return { success: true, entries: [] };

    const snapshot = await get(ref(db, tenantPath(tenantId, 'measurements', memberId)));

    if (!snapshot.exists()) {
      return { success: true, entries: [] };
    }

    const entries = [];
    snapshot.forEach((child) => {
      entries.push({ id: child.key, ...child.val() });
    });

    entries.sort((a, b) => new Date(a.measuredAt) - new Date(b.measuredAt));

    return { success: true, entries };
  } catch (error) {
    console.error('Error loading weight history:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to load weight history'
    };
  }
};

// Remove a single reading (e.g. entered by mistake)
export const deleteWeightEntry = async (tenantId, memberId, entryId) => {
  try {
    if (!tenantId || !memberId || !entryId) {
      return { success: false, error: 'invalid_request', message: 'Missing reference' };
    }

    await remove(ref(db, tenantPath(tenantId, 'measurements', memberId, entryId)));
    return { success: true, message: 'Reading deleted' };
  } catch (error) {
    console.error('Error deleting weight entry:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to delete reading'
    };
  }
};
